// @src/pre-deployment/src/components/shared/SafeErrorBoundary.js v1.3
// v1.1: Raw-only path for non-deprecated components; deprecated still use ErrorLoggingService
// v1.2: Remove ClozeExercise from deprecated list (per firestore_path_audit plan; component lives in exercises/)
// v1.3: DEPRECATE ErrorLoggingService entirely. All errors now go to RawErrorService.
// - Provides Retry and Back to Menu buttons; validation details in development only

import React from 'react';
import { useNavigate } from 'react-router-dom';

import { writeRawError } from '../ErrorHandling/AutomatedErrorSystem/RawErrorService';

// ============================================================================
// SAFE ERROR BOUNDARY (CLASS COMPONENT - Required for componentDidCatch)
// ============================================================================

class SafeErrorBoundaryClass extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  async componentDidCatch(error, errorInfo) {
    console.group('SafeErrorBoundary: Error Caught');
    console.error('Error:', error);
    console.log('Component Stack:', errorInfo?.componentStack);
    console.groupEnd();

    this.setState({ errorInfo });

    const context = {
      operationType: 'component_error',
      severity: 'critical',
      componentStack: errorInfo?.componentStack,
      url: typeof window !== 'undefined' ? window.location.href : 'N/A',
      isValidationError: error?.isValidationError || false,
      validationErrors: error?.errors || [],
      componentName: this.props.componentName || 'Unknown',
      level: this.props.level,
      chapter: this.props.chapter
    };

    try {
      // v1.3: All components use writeRawError now. Legacy ErrorLoggingService is decommissioned.
      const rawId = await writeRawError(error, context);
      this.setState({ errorId: rawId });
      console.log('Error written to raw (AutomatedErrorSystem):', rawId);
    } catch (loggingError) {
      console.error('Failed to log error:', loggingError);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, errorId: null });
    // Force re-render by clearing error state
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  render() {
    if (this.state.hasError) {
      // Render the error UI component
      return (
        <SafeErrorUI
          error={this.state.error}
          errorId={this.state.errorId}
          onRetry={this.handleRetry}
          navigate={this.props.navigate}
          componentName={this.props.componentName}
        />
      );
    }

    return this.props.children;
  }
}

// ============================================================================
// STYLES (must be before SafeErrorUI for no-use-before-define)
// ============================================================================

const styles = {
  container: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: '20px', backgroundColor: '#f5f5f5', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  card: { backgroundColor: '#ffffff', borderRadius: '16px', padding: '40px', maxWidth: '500px', width: '100%', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)', textAlign: 'center' },
  header: { marginBottom: '24px' },
  iconCircle: { width: '60px', height: '60px', borderRadius: '50%', backgroundColor: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px auto' },
  icon: { fontSize: '28px', fontWeight: 'bold', color: '#dc2626' },
  title: { fontSize: '24px', fontWeight: '600', color: '#1f2937', margin: '0' },
  message: { fontSize: '16px', color: '#6b7280', marginBottom: '24px', lineHeight: '1.5' },
  componentInfo: { fontSize: '12px', color: '#9ca3af', marginBottom: '16px' },
  actions: { display: 'flex', flexDirection: 'column', gap: '12px' },
  primaryButton: { padding: '14px 24px', fontSize: '16px', fontWeight: '600', color: '#ffffff', backgroundColor: '#3b82f6', border: 'none', borderRadius: '8px', cursor: 'pointer', transition: 'background-color 0.2s' },
  secondaryButton: { padding: '14px 24px', fontSize: '16px', fontWeight: '600', color: '#374151', backgroundColor: '#e5e7eb', border: 'none', borderRadius: '8px', cursor: 'pointer', transition: 'background-color 0.2s' },
  tertiaryButton: { padding: '14px 24px', fontSize: '14px', fontWeight: '500', color: '#6b7280', backgroundColor: 'transparent', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', transition: 'background-color 0.2s' },
  errorId: { fontSize: '11px', color: '#9ca3af', marginTop: '24px' },
  devSection: { backgroundColor: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '8px', padding: '16px', marginBottom: '24px', textAlign: 'left' },
  devHeader: { fontSize: '12px', fontWeight: '600', color: '#92400e', marginBottom: '8px', textTransform: 'uppercase' },
  validationErrors: { fontSize: '13px', color: '#78350f' },
  validationErrorItem: { display: 'flex', flexDirection: 'column', padding: '8px', marginTop: '8px', backgroundColor: '#fffbeb', borderRadius: '4px', gap: '4px' },
  validationPath: { fontFamily: 'monospace', fontWeight: '600', color: '#b45309' },
  validationExpected: { fontSize: '12px', color: '#92400e' },
  validationMessage: { fontSize: '12px', color: '#78350f', fontStyle: 'italic' },
  contextInfo: { marginTop: '12px', fontSize: '12px' },
  contextPre: { backgroundColor: '#fffbeb', padding: '8px', borderRadius: '4px', fontSize: '11px', overflow: 'auto', maxHeight: '150px' },
};

// ============================================================================
// SAFE ERROR UI COMPONENT
// ============================================================================

const SafeErrorUI = ({ error, errorId, onRetry, navigate, componentName }) => {
  const isDevelopment = process.env.NODE_ENV === 'development';
  const isValidationError = error?.isValidationError;

  // Get user-friendly message; always surface real error when available
  const getUserFriendlyMessage = () => {
    if (!error) return 'An unexpected error occurred.';

    if (isValidationError) {
      return 'The content could not be loaded due to missing or invalid data.';
    }

    if (error.message?.includes('ERR_INSUFFICIENT_RESOURCES') || error.message?.includes('InsufficientResources')) {
      return 'Too many requests. Please wait a moment and try again.';
    }

    if (error.message?.includes('timeout')) {
      return 'The request took too long. Please check your connection and try again.';
    }

    if (error.message?.includes('network') || error.message?.includes('fetch')) {
      return 'Unable to connect to the server. Please check your internet connection.';
    }

    return 'Something went wrong while loading the content.';
  };

  // Get validation error details for development
  const getValidationDetails = () => {
    if (!isValidationError || !error.errors || error.errors.length === 0) {
      return null;
    }

    return error.errors.map((err, index) => (
      <div key={index} style={styles.validationErrorItem}>
        <span style={styles.validationPath}>{err.path}</span>
        <span style={styles.validationExpected}>Expected: {err.expected}</span>
        {err.message && <span style={styles.validationMessage}>{err.message}</span>}
      </div>
    ));
  };

  const handleBackToMenu = () => {
    if (navigate) {
      navigate('/map');
    } else {
      window.location.href = '/map';
    }
  };

  const handleGoBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      handleBackToMenu();
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.iconCircle}>
            <span style={styles.icon}>!</span>
          </div>
          <h1 style={styles.title}>Error Loading Content</h1>
        </div>
        {/* Message */}
        <p style={styles.message}>{getUserFriendlyMessage()}</p>

        {/* Real error (always shown for debugging) */}
        {error?.message && (
          <pre style={{ ...styles.contextPre, marginBottom: '16px', fontSize: '12px', maxHeight: '80px', overflow: 'auto' }}>
            {error.message}
          </pre>
        )}

        {/* Component info (subtle) */}
        {componentName && (
          <p style={styles.componentInfo}>
            Error in: {componentName}
          </p>
        )}

        {/* Development-only: Validation details */}
        {isDevelopment && isValidationError && (
          <div style={styles.devSection}>
            <div style={styles.devHeader}>Development Info</div>
            <div style={styles.validationErrors}>
              <strong>Validation Errors:</strong>
              {getValidationDetails()}
            </div>
            {error.context && (
              <div style={styles.contextInfo}>
                <strong>Context:</strong>
                <pre style={styles.contextPre}>
                  {JSON.stringify(error.context, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div style={styles.actions}>
          <button onClick={onRetry} style={styles.primaryButton}>
            Try Again
          </button>
          <button onClick={handleBackToMenu} style={styles.secondaryButton}>
            Back to Menu
          </button>
          <button onClick={handleGoBack} style={styles.tertiaryButton}>
            Go Back
          </button>
        </div>

        {/* Error ID for support */}
        {errorId && (
          <p style={styles.errorId}>
            Error ID: {errorId}
          </p>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// WRAPPER WITH NAVIGATION
// ============================================================================

/**
 * SafeErrorBoundary with React Router navigation support
 * Use this as the default export for route-level error boundaries
 */
const SafeErrorBoundary = (props) => {
  let navigate;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    navigate = useNavigate();
  } catch (e) {
    // Not inside a Router context, navigation will fallback to window.location
    navigate = null;
  }

  return (
    <SafeErrorBoundaryClass {...props} navigate={navigate} />
  );
};

// Export both versions
export { SafeErrorBoundaryClass, SafeErrorUI };
export default SafeErrorBoundary;
